import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { debug } from '@/utils/debug';
import type { AppConfiguration } from '@/types/api';

export interface AccountSettings {
  useConversionBasedRoas: boolean;
  revenuePerClick: number | null;
  totalBudget: number;
  targetDensity: number;
  minDensity: number;
  targetAbsDensityChange: number;
}

export interface AccountSettingsConfigSlice {
  use_conversion_based_roas: boolean;
  revenue_per_click: number | null;
  global_accounts_allocatable_budget: number;
  target_density: number;
  min_density: number;
  target_abs_density_change: number;
}

export interface AccountSettingsStore extends AccountSettings {
  setUseConversionBasedRoas: (value: boolean) => void;
  setRevenuePerClick: (value: number | null) => void;
  setTotalBudget: (value: number) => void;
  setTargetDensity: (value: number) => void;
  setMinDensity: (value: number) => void;
  setTargetAbsDensityChange: (value: number) => void;

  originalSettings: AccountSettings | null;
  hasRoasSettingsChange: boolean;
  hasTotalBudgetChange: boolean;
  hasTargetDensityChange: boolean;
  hasMinDensityChange: boolean;
  hasTargetAbsDensityChangeRateChange: boolean;

  hydrateFromConfig: (config: AppConfiguration['configuration'] | undefined | null) => void;
  // `existing` is unused here — account-level fields are full-overwrite,
  // not merged. Accepted for interface parity with other slice providers.
  // Return type is widened to Partial<AppConfiguration['configuration']> so
  // the store satisfies AppConfigSliceProvider's index-signature requirement
  // (AppConfiguration['configuration'] has `[key: string]: unknown`).
  toConfigSlice: (existing?: AppConfiguration['configuration'] | null) => Partial<AppConfiguration['configuration']> & AccountSettingsConfigSlice;
  discardChanges: () => void;
  markSaved: () => void;
}

/**
 * Production-default simplex constants. Owned by the account store (which is
 * the canonical home for these now-persisted settings) and re-imported by
 * BudgetOptimizerSimple.tsx as send-boundary clamp fallbacks. Inlining the
 * literal in both places was a theme #16 drift risk — a future tweak to one
 * site would silently desync the other.
 *
 * Match the backend's `optimization.budgetAllocation` constants used by the
 * weekly Cloud Run job; bump together or the dev panel preview diverges from
 * the production run.
 */
export const PROD_MIN_DENSITY = 0.05;
export const PROD_TARGET_ABS_DENSITY_CHANGE = 0.1;

export const DEFAULT_ACCOUNT_SETTINGS: AccountSettings = {
  useConversionBasedRoas: true,
  revenuePerClick: null,
  totalBudget: 0,
  targetDensity: 0.3,
  // Used when the saved config has no min_density key (legacy configs
  // predating this field).
  minDensity: PROD_MIN_DENSITY,
  // Used when the saved config has no target_abs_density_change key.
  targetAbsDensityChange: PROD_TARGET_ABS_DENSITY_CHANGE,
};

const roundCents = (value: number): number => Math.round(value * 100) / 100;
const nearlyEqual = (a: number, b: number, epsilon = 0.001): boolean => Math.abs(a - b) < epsilon;

const computeRoasChange = (settings: AccountSettings, original: AccountSettings | null): boolean => {
  if (original === null) return false;
  return (
    settings.useConversionBasedRoas !== original.useConversionBasedRoas ||
    settings.revenuePerClick !== original.revenuePerClick
  );
};

export const useAccountSettingsStore = create<AccountSettingsStore>()(
  devtools(
    (set, get) => ({
      ...DEFAULT_ACCOUNT_SETTINGS,
      originalSettings: null,
      hasRoasSettingsChange: false,
      hasTotalBudgetChange: false,
      hasTargetDensityChange: false,
      hasMinDensityChange: false,
      hasTargetAbsDensityChangeRateChange: false,

      setUseConversionBasedRoas: (value) => {
        const { revenuePerClick, originalSettings } = get();
        set({
          useConversionBasedRoas: value,
          hasRoasSettingsChange: computeRoasChange(
            { ...get(), useConversionBasedRoas: value, revenuePerClick },
            originalSettings,
          ),
        });
        debug.log('AccountSettings: useConversionBasedRoas updated', { value });
      },

      setRevenuePerClick: (value) => {
        const { useConversionBasedRoas, originalSettings } = get();
        set({
          revenuePerClick: value,
          hasRoasSettingsChange: computeRoasChange(
            { ...get(), useConversionBasedRoas, revenuePerClick: value },
            originalSettings,
          ),
        });
        debug.log('AccountSettings: revenuePerClick updated', { value });
      },

      setTotalBudget: (value) => {
        const rounded = roundCents(value);
        const { originalSettings } = get();
        // Auto-sync (driven by per-campaign edits) is still a pending change
        // against the saved config and must light up the SaveChangesBar —
        // mirrors the old googleStore.setTotalBudget contract.
        const hasChange = originalSettings !== null && !nearlyEqual(rounded, originalSettings.totalBudget);
        set({ totalBudget: rounded, hasTotalBudgetChange: hasChange });
      },

      setTargetDensity: (value) => {
        const { originalSettings } = get();
        const hasChange = originalSettings !== null && !nearlyEqual(value, originalSettings.targetDensity);
        set({ targetDensity: value, hasTargetDensityChange: hasChange });
        debug.log('AccountSettings: targetDensity updated', { value });
      },

      setMinDensity: (value) => {
        const { originalSettings } = get();
        const hasChange = originalSettings !== null && !nearlyEqual(value, originalSettings.minDensity);
        set({ minDensity: value, hasMinDensityChange: hasChange });
        debug.log('AccountSettings: minDensity updated', { value });
      },

      setTargetAbsDensityChange: (value) => {
        const { originalSettings } = get();
        const hasChange = originalSettings !== null && !nearlyEqual(value, originalSettings.targetAbsDensityChange);
        set({ targetAbsDensityChange: value, hasTargetAbsDensityChangeRateChange: hasChange });
        debug.log('AccountSettings: targetAbsDensityChange updated', { value });
      },

      hydrateFromConfig: (config) => {
        const hydrated: AccountSettings = {
          useConversionBasedRoas: config?.use_conversion_based_roas ?? DEFAULT_ACCOUNT_SETTINGS.useConversionBasedRoas,
          revenuePerClick: config?.revenue_per_click ?? DEFAULT_ACCOUNT_SETTINGS.revenuePerClick,
          totalBudget: config?.global_accounts_allocatable_budget ?? DEFAULT_ACCOUNT_SETTINGS.totalBudget,
          targetDensity: config?.target_density ?? DEFAULT_ACCOUNT_SETTINGS.targetDensity,
          minDensity: config?.min_density ?? DEFAULT_ACCOUNT_SETTINGS.minDensity,
          targetAbsDensityChange:
            config?.target_abs_density_change ?? DEFAULT_ACCOUNT_SETTINGS.targetAbsDensityChange,
        };
        set({
          ...hydrated,
          originalSettings: hydrated,
          hasRoasSettingsChange: false,
          hasTotalBudgetChange: false,
          hasTargetDensityChange: false,
          hasMinDensityChange: false,
          hasTargetAbsDensityChangeRateChange: false,
        });
      },

      toConfigSlice: (existing) => {
        const state = get();
        // Pre-hydrate guard: if `originalSettings` is null, the store
        // hasn't loaded the member's saved values yet (mount race, or a
        // campaign-only save fired before useAppConfigQuery resolved).
        // Returning store defaults would overwrite the member's
        // previously-saved account-level settings with `totalBudget=0`,
        // `revenuePerClick=null`, etc. Pass through whatever's already
        // in the existing config (falling back to defaults only when
        // existing is also missing the key — true new-user case).
        if (state.originalSettings === null) {
          return {
            use_conversion_based_roas:
              existing?.use_conversion_based_roas ?? DEFAULT_ACCOUNT_SETTINGS.useConversionBasedRoas,
            revenue_per_click:
              existing?.revenue_per_click ?? DEFAULT_ACCOUNT_SETTINGS.revenuePerClick,
            global_accounts_allocatable_budget:
              existing?.global_accounts_allocatable_budget ?? DEFAULT_ACCOUNT_SETTINGS.totalBudget,
            target_density:
              existing?.target_density ?? DEFAULT_ACCOUNT_SETTINGS.targetDensity,
            min_density:
              existing?.min_density ?? DEFAULT_ACCOUNT_SETTINGS.minDensity,
            target_abs_density_change:
              existing?.target_abs_density_change ?? DEFAULT_ACCOUNT_SETTINGS.targetAbsDensityChange,
          };
        }
        return {
          use_conversion_based_roas: state.useConversionBasedRoas,
          revenue_per_click: state.revenuePerClick,
          global_accounts_allocatable_budget: state.totalBudget,
          target_density: state.targetDensity,
          min_density: state.minDensity,
          target_abs_density_change: state.targetAbsDensityChange,
        };
      },

      discardChanges: () => {
        const { originalSettings } = get();
        if (originalSettings === null) {
          // No baseline → just clear dirty flags; current values stay (matches
          // the prior googleStore.discardChanges behavior when no original).
          set({
            hasRoasSettingsChange: false,
            hasTotalBudgetChange: false,
            hasTargetDensityChange: false,
            hasMinDensityChange: false,
            hasTargetAbsDensityChangeRateChange: false,
          });
          return;
        }
        set({
          ...originalSettings,
          hasRoasSettingsChange: false,
          hasTotalBudgetChange: false,
          hasTargetDensityChange: false,
          hasMinDensityChange: false,
          hasTargetAbsDensityChangeRateChange: false,
        });
      },

      markSaved: () => {
        const {
          useConversionBasedRoas,
          revenuePerClick,
          totalBudget,
          targetDensity,
          minDensity,
          targetAbsDensityChange,
        } = get();
        set({
          originalSettings: {
            useConversionBasedRoas,
            revenuePerClick,
            totalBudget,
            targetDensity,
            minDensity,
            targetAbsDensityChange,
          },
          hasRoasSettingsChange: false,
          hasTotalBudgetChange: false,
          hasTargetDensityChange: false,
          hasMinDensityChange: false,
          hasTargetAbsDensityChangeRateChange: false,
        });
      },
    }),
    { name: 'AccountSettingsStore' },
  ),
);
